/**
 * 送往 GlitchTip 前遮蔽敏感或過大的欄位。
 * - password / secret / token：避免登入憑證隨錯誤事件外流
 * - photoBase64：註冊頭像轉 base64 後可達 1.4MB，超過事件大小上限會導致整筆錯誤被丟棄
 */
const REDACT_KEY_PATTERN = /password|secret|token|photoBase64|code_?verifier/i
const REDACTED = '[Redacted]'
const TRUNCATED = '[Truncated]'
const MAX_DEPTH = 6
const MAX_STRING_LENGTH = 1000

const redactValue = (value: unknown, depth = 0): unknown => {
  if (value === null || typeof value !== 'object') {
    return value
  }

  // 超過深度上限時不可回傳原始子樹，否則深層的 password / token 會未遮蔽外流
  if (depth > MAX_DEPTH) {
    return TRUNCATED
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1))
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, val]) => [
      key,
      REDACT_KEY_PATTERN.test(key) ? REDACTED : redactValue(val, depth + 1)
    ])
  )
}

// OAuth token 端點的 body 是 form-urlencoded 字串（含 password、code_verifier），需逐一遮蔽 key
const URL_ENCODED_PATTERN = /^[^=&\s]+=[^&\s]*(&[^=&\s]+=[^&\s]*)*$/

const redactUrlEncoded = (data: string): string => {
  const redacted = new URLSearchParams()
  new URLSearchParams(data).forEach((value, key) => {
    redacted.append(key, REDACT_KEY_PATTERN.test(key) ? REDACTED : value)
  })
  return redacted.toString()
}

/**
 * 遮蔽 axios 的 request/response data。
 * axios 送出前會把 body 序列化成字串，故字串先嘗試以 JSON、form-urlencoded 解析後再遮蔽。
 */
export const redactSensitive = (data: unknown): unknown => {
  if (typeof data === 'string') {
    try {
      return redactValue(JSON.parse(data))
    } catch {
      if (URL_ENCODED_PATTERN.test(data)) {
        return redactUrlEncoded(data)
      }
      return data.length > MAX_STRING_LENGTH ? `${data.slice(0, MAX_STRING_LENGTH)}…[truncated]` : data
    }
  }

  return redactValue(data)
}
